import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { DarkModeToggle } from '../common/DarkModeToggle';
import './Sidebar.css';

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, login, logout } = useAuth();
  const { t } = useI18n();
  const { shouldShowClientUI, canManageUsers, canViewAuditLog, isSuperAdmin } = useClientAccess();

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleAuthAction = () => {
    if (isAuthenticated) {
      logout();
    } else {
      login();
    }
  };

  const getNameParts = (fullName: string): { firstName: string; lastName: string } => {
    if (!fullName) return { firstName: 'User', lastName: '' };
    const parts = fullName.trim().split(/\s+/);
    return {
      firstName: parts[0] || 'User',
      lastName: parts.slice(1).join(' ')
    };
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="sidebar">
      <nav className="sidebar-nav">
        {isAuthenticated ? (
          <>
            <button
              className={`sidebar-button ${isActive('/dashboard') ? 'active' : ''}`}
              onClick={() => handleNavigation('/dashboard')}
            >
              <span className="sidebar-icon">📊</span>
              <span className="sidebar-text">{t('nav.dashboard')}</span>
            </button>

            <button
              className={`sidebar-button ${isActive('/devices') ? 'active' : ''}`}
              onClick={() => handleNavigation('/devices')}
            >
              <span className="sidebar-icon">📱</span>
              <span className="sidebar-text">{t('nav.devices')}</span>
            </button>

            {shouldShowClientUI && (
              <button
                className={`sidebar-button ${isActive('/clients') ? 'active' : ''}`}
                onClick={() => handleNavigation('/clients')}
              >
                <span className="sidebar-icon">👥</span>
                <span className="sidebar-text">{t('nav.clients')}</span>
              </button>
            )}

            <button
              className={`sidebar-button ${isActive('/config-templates') ? 'active' : ''}`}
              onClick={() => handleNavigation('/config-templates')}
            >
              <span className="sidebar-icon">📋</span>
              <span className="sidebar-text">{t('nav.configTemplates')}</span>
            </button>

            <button
              className={`sidebar-button ${isActive('/device-types') ? 'active' : ''}`}
              onClick={() => handleNavigation('/device-types')}
            >
              <span className="sidebar-icon">🏷️</span>
              <span className="sidebar-text">{t('nav.deviceTypes')}</span>
            </button>

            <button
              className={`sidebar-button ${isActive('/ota-updates') ? 'active' : ''}`}
              onClick={() => handleNavigation('/ota-updates')}
            >
              <span className="sidebar-icon">⬆️</span>
              <span className="sidebar-text">{t('nav.otaUpdates')}</span>
            </button>

            {canManageUsers && (
              <button
                className={`sidebar-button ${isActive('/users') ? 'active' : ''}`}
                onClick={() => handleNavigation('/users')}
              >
                <span className="sidebar-icon">🔐</span>
                <span className="sidebar-text">{t('nav.users')}</span>
              </button>
            )}

            {canViewAuditLog && (
              <button
                className={`sidebar-button ${isActive('/audit-log') ? 'active' : ''}`}
                onClick={() => handleNavigation('/audit-log')}
              >
                <span className="sidebar-icon">📜</span>
                <span className="sidebar-text">{t('nav.auditLog')}</span>
              </button>
            )}

            <div className="sidebar-spacer"></div>

            <button
              className="sidebar-button user-profile-button"
              onClick={handleAuthAction}
              title={`${t('auth.signedInAs')} ${user?.name || 'User'} — ${t('auth.signOut')}`}
            >
              <span className="sidebar-icon">👤</span>
              <span className="sidebar-text user-name-text">
                <span className="user-first-name">{user ? getNameParts(user.name).firstName : 'User'}</span>
                {user && getNameParts(user.name).lastName && (
                  <span className="user-last-name">{getNameParts(user.name).lastName}</span>
                )}
              </span>
            </button>

            <div className="sidebar-bottom-row">
              {isSuperAdmin && (
                <button
                  type="button"
                  className={`sidebar-gear ${isActive('/settings') ? 'active' : ''}`}
                  onClick={() => handleNavigation('/settings')}
                  aria-label={t('nav.settings')}
                  title={t('nav.settings')}
                >
                  <svg
                    className="sidebar-gear-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              )}
              <DarkModeToggle />
            </div>
          </>
        ) : (
          <>
            <div className="sidebar-welcome">
              <div className="sidebar-welcome-icon">🔒</div>
              <div className="sidebar-welcome-text">
                <h3>{t('auth.welcome')}</h3>
                <p>{t('auth.pleaseSignIn')}</p>
              </div>
            </div>

            <div className="sidebar-spacer"></div>

            <button
              className="sidebar-button login-button"
              onClick={handleAuthAction}
            >
              <span className="sidebar-icon">🔑</span>
              <span className="sidebar-text">{t('nav.login')}</span>
            </button>

            <div className="sidebar-bottom-row">
              <DarkModeToggle />
            </div>
          </>
        )}
      </nav>
    </div>
  );
};